import type { Ballot, FlowCell, FlowRow, Round, RoundNames } from '../schemas/entities';
import type { Format, Sheet } from '../schemas/format';
import { ALL_SHEET_ID, singleSheet } from './sheets';
import { CX_ROW_ID, cellId } from '../usecases/flow';
import { segmentTitle, sheetTitle } from './sides';

/**
 * An argument = the row where it's first written in the sheet's first column, plus the rows under it whose
 * first column is empty (one row per response, so each can be extended or dropped on its own).
 */
export interface ArgumentGroup {
  headRowId: string;
  rowIds: string[];
  /** The argument as first written (its case column on the head row). */
  text: string;
  /** The case column it's written in (C1 or C2). */
  caseSegId: string;
}

/**
 * Group rows (already in display order, e.g. from rowsForSheet) into arguments. A row starts an argument when one of
 * the sheet's case columns (its constructives: C1 on a per-case sheet, C1 and C2 on the single sheet) has text.
 */
export const argumentGroups = (sheet: Sheet, format: Format, rows: FlowRow[], cells: Map<string, FlowCell>): ArgumentGroup[] => {
  const caseCols = sheet.segmentIds.filter((id) => format.segments.find((s) => s.id === id)?.role === 'constructive');
  if (caseCols.length === 0) caseCols.push(sheet.segmentIds[0]!);
  const groups: ArgumentGroup[] = [];
  for (const r of rows) {
    const col = caseCols.find((id) => cells.get(cellId(r.id, id))?.text.trim());
    if (col || groups.length === 0) groups.push({ headRowId: r.id, rowIds: [r.id], text: col ? cells.get(cellId(r.id, col))!.text.trim() : '', caseSegId: col ?? caseCols[0]! });
    else groups.at(-1)!.rowIds.push(r.id);
  }
  return groups.filter((g) => g.text);
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Before anything leaves the device: debater names become "[PRO 1]"-style tokens and team codes become "[PRO]"/"[CON]".
 * Returns the redacted text plus the reverse map, so the app can show real team codes in what comes back.
 */
export const redactor = (round: Pick<Round, 'teamA' | 'teamB' | 'teamASide' | 'format'>, names?: Pick<RoundNames, 'teamA' | 'teamB'> | null) => {
  const label = (team: 'A' | 'B') => {
    if (round.teamASide === null) return `TEAM ${team}`;
    const side = team === 'A' ? round.teamASide : 1 - round.teamASide;
    return round.format.sideLabels[side as 0 | 1].toUpperCase();
  };
  const pairs: [string, string][] = [];
  for (const team of ['A', 'B'] as const) {
    (names?.[team === 'A' ? 'teamA' : 'teamB'] ?? []).forEach((n, i) => {
      const t = n.trim();
      if (t.length >= 2) {
        pairs.push([t, `[${label(team)} ${i + 1}]`]);
        // First names alone are how judges usually write them.
        const firstName = t.split(/\s+/)[0]!;
        if (firstName !== t && firstName.length >= 3) pairs.push([firstName, `[${label(team)} ${i + 1}]`]);
      }
    });
    const code = (team === 'A' ? round.teamA : round.teamB).code.trim();
    if (code) pairs.push([code, `[${label(team)}]`]);
  }
  // Longest first so "Ana Lopez" wins over "Ana".
  pairs.sort((a, b) => b[0].length - a[0].length);
  const redact = (text: string) =>
    pairs.reduce((s, [from, to]) => s.replace(new RegExp(`(?<![\\p{L}\\p{N}])${escapeRe(from)}(?![\\p{L}\\p{N}])`, 'giu'), to), text);
  const codes: [string, string][] = (['A', 'B'] as const).map((t) => [`[${label(t)}]`, (t === 'A' ? round.teamA : round.teamB).code]);
  /** Put team codes back (names stay redacted: they never came from the device's copy). */
  const restore = (text: string) => codes.reduce((s, [token, code]) => s.split(token).join(code), text);
  /** Token for a debater, e.g. "A0" → "[PRO 1]". */
  const speakerToken = (key: string) => `[${label(key[0] as 'A' | 'B')} ${Number(key.slice(1)) + 1}]`;
  const teamToken = (team: 'A' | 'B') => `[${label(team)}]`;
  /**
   * For text the judge will use (ballot drafts): tokens back to real names, on the device only. A debater without a
   * name becomes "Lincoln AB's first speaker".
   */
  const restoreNames = (text: string) => {
    let out = text;
    for (const team of ['A', 'B'] as const) {
      const code = (team === 'A' ? round.teamA : round.teamB).code;
      for (let i = 0; i < 5; i++) {
        const name = (team === 'A' ? names?.teamA : names?.teamB)?.[i]?.trim();
        out = out.split(`[${label(team)} ${i + 1}]`).join(name || `${code}'s ${['first', 'second', 'third', 'fourth', 'fifth'][i]} speaker`);
      }
    }
    return restore(out);
  };
  return { redact, restore, restoreNames, speakerToken, teamToken };
};

/**
 * The whole round as plain text for a post-round recap: every sheet, argument by argument, speech by speech,
 * with the judge's marks. Redacted.
 */
export const flowAsText = (
  round: Round,
  rows: FlowRow[],
  cells: Map<string, FlowCell>,
  names: Pick<RoundNames, 'teamA' | 'teamB'> | null | undefined,
  ballot: Pick<Ballot, 'winnerSide'> | null | undefined,
): string => {
  const { redact } = redactor(round, names);
  const out: string[] = round.resolution ? [`Resolution: ${round.resolution}`] : [];
  const seg = (id: string) => round.format.segments.find((s) => s.id === id)!;
  for (const sheet of [singleSheet(round.format), ...round.format.sheets]) {
    const groups = argumentGroups(sheet, round.format, rows.filter((r) => r.sheetId === sheet.id).sort((a, b) => a.rank - b.rank), cells);
    if (groups.length === 0) continue;
    out.push(`## Sheet: ${sheet.id === ALL_SHEET_ID ? 'Flow (both cases)' : sheetTitle(round, sheet.title)}`);
    groups.forEach((g, i) => {
      const title = rows.find((r) => r.id === g.headRowId)?.title;
      out.push(`### Argument ${i + 1}${title ? `: ${title}` : ''}`);
      for (const rowId of g.rowIds) {
        for (const segId of sheet.segmentIds) {
          const c = cells.get(cellId(rowId, segId));
          if (!c || (!c.text.trim() && c.marks.length === 0)) continue;
          const marks = c.marks.length ? ` [judge marked: ${c.marks.join(', ')}]` : '';
          out.push(`- ${segmentTitle(round, seg(segId))}: ${c.text.trim().replace(/\n+/g, ' / ')}${marks}`);
        }
      }
    });
  }
  // Cross-ex notes: one box per questioning period.
  for (const seg of round.format.segments.filter((s) => s.kind === 'questioning')) {
    const c = cells.get(cellId(CX_ROW_ID, seg.id));
    if (c?.text.trim()) out.push(`\n## ${segmentTitle(round, seg)} notes\n${c.text.trim()}${c.marks.length ? ` [judge marked: ${c.marks.join(', ')}]` : ''}`);
  }
  const decision = ballot?.winnerSide === 0 || ballot?.winnerSide === 1 ? round.format.sideLabels[ballot.winnerSide].toUpperCase() : null;
  if (decision) out.push(`\nThe judge's decision: ${decision} won.`);
  return redact(out.join('\n'));
};
